import React, { useEffect, useMemo } from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import { useSetAtom } from "jotai";
import {
  activeSSMLTagsAtom,
  languageOptionsAtom,
  setNotificationAtom,
  styleEmotionNames,
  voicesDescriptionAtom,
} from "atoms";
import { ReactComponent as CopyIcon } from "assets/icons/tts-custom-editor/copy.svg";
import { ReactComponent as DeleteIcon } from "assets/icons/tts-custom-editor/delete.svg";
import { TTSCustomEditorColors } from "shared/constants";
import CustomIconMenu from "../../components/CustomIconMenu";
import { isEditorEmpty } from "shared/utils/tts-editor-helper";
import { stripSpeakTags, transformToSSML } from "shared/utils/ssml-helper";
import { decodeUmlautEntitiesOnly } from "shared/utils/tts-editor-helper";
import { isHtmlViewAtom, isSsmlViewEditable } from "atoms";
import { useAtomValue } from "jotai";
import { AlertColor } from "@mui/material";
import { validateSSML } from "../../utils/ssmlValidator";

interface CopyAndClearMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
  action: "copy" | "clear";
  onClear?: () => void;
}

export default function CopyAndClearMenu({
  editorRef,
  action,
  onClear,
}: CopyAndClearMenuProps): JSX.Element {
  const { t } = useTranslation();
  const setNotification = useSetAtom(setNotificationAtom);
  const setActiveSSMLTags = useSetAtom(activeSSMLTagsAtom);
  const isHtmlView = useAtomValue(isHtmlViewAtom);
  const ssmlViewEditable = useAtomValue(isSsmlViewEditable);

  const voicesDescription = useAtomValue(voicesDescriptionAtom);
  const voiceNames = voicesDescription.map((v) => v.voiceName);
  const emotionNames = useAtomValue(styleEmotionNames);
  const languageOptions = useAtomValue(languageOptionsAtom);
  const languageValues = useMemo(
    () => languageOptions.map((o) => o.value),
    [languageOptions]
  );

  useEffect(() => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    const editorBody = editor.getBody();
    if (!editorBody) return;

    // This function is being used for Keyboard Copy
    const handleCopy = (e: ClipboardEvent) => {
      const selectedHtml = editor.selection.getContent({ format: "html" });
      const selectedText = editor.selection.getContent({ format: "text" });

      if (!selectedText.trim()) {
        return;
      }

      const probe = document.createElement("div");
      probe.innerHTML = selectedHtml;
      const selectionHasSSML = probe.querySelector("[data-ssml]") !== null;

      const selectedNode = editor.selection.getNode();
      const parentSSMLElement = selectedNode.closest("[data-ssml]");
      const nodeHasSSML = !!parentSSMLElement;

      const textContainsSSMLTags = /<[^>]+>/.test(selectedText);

      if (!selectionHasSSML && !nodeHasSSML && !textContainsSSMLTags) {
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      let contentToCopy = "";

      if (isHtmlView) {
        contentToCopy = selectedText.trim();

        if (!contentToCopy) {
          return;
        }

        // If the text contains SSML tags, validate it
        if (textContainsSSMLTags) {
          const validationResult = validateSSML(contentToCopy, false, t, {
            dynamicAllowedValues: {
              voiceNames,
              styleNames: emotionNames,
              languageValues,
            },
          });
          if (!validationResult.valid) {
            setNotification({
              severity: "error" as AlertColor,
              message: t("tts.invalidSsmlCopy"),
            });
            navigator.clipboard.writeText("").catch(() => { });
            return;
          }
        }

        // Wrap with <speak>, stripping any existing speak tags first
        contentToCopy = `<speak>${stripSpeakTags(contentToCopy)}</speak>`;
        navigator.clipboard.writeText(contentToCopy);
      } else {
        let htmlToProcess = selectedHtml;

        if (nodeHasSSML || selectionHasSSML) {
          // Get the selection and check if it's a complete selection
          const sel = window.getSelection();
          const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;

          const entireContent = editor.getContent({ format: "html" });
          const selectedTextTrimmed = selectedText.trim();

          const normalizeHtml = (html: string) => {
            return html.replace(/\s+/g, " ").replace(/>\s+</g, "><").trim();
          };

          const normalizedSelectedHtml = normalizeHtml(selectedHtml);
          const normalizedEntireContent = normalizeHtml(entireContent);

          // Check if selection is actually the entire content
          const htmlMatches =
            normalizedSelectedHtml === normalizedEntireContent;

          // Additional check: if selection range covers the entire body
          let rangeCoversEntireBody = false;
          if (range && editorBody) {
            try {
              const editorDoc = editorBody.ownerDocument;
              if (editorDoc) {
                const bodyRange = editorDoc.createRange();
                bodyRange.selectNodeContents(editorBody);

                rangeCoversEntireBody =
                  range.compareBoundaryPoints(
                    Range.START_TO_START,
                    bodyRange
                  ) === 0 &&
                  range.compareBoundaryPoints(Range.END_TO_END, bodyRange) ===
                  0;
              }
            } catch (e) {
              // If range comparison fails, fall back to HTML comparison
              rangeCoversEntireBody = false;
            }
          }

          // Determine if this is a full selection
          const isFullContentSelected = htmlMatches || rangeCoversEntireBody;

          if (isFullContentSelected) {
            // Use the entire content
            htmlToProcess = entireContent;
          } else {
            // Find all SSML ancestors of the selection
            let outerMostSSML: HTMLElement | null = null;
            if (parentSSMLElement) {
              let current: HTMLElement | null =
                parentSSMLElement as HTMLElement;
              while (current && current !== editorBody) {
                if (current.hasAttribute("data-ssml")) {
                  outerMostSSML = current;
                }
                current = current.parentElement;
              }

              // Check if the selected text matches the outermost SSML element's text
              if (
                outerMostSSML &&
                outerMostSSML.textContent?.trim() === selectedTextTrimmed
              ) {
                htmlToProcess = outerMostSSML.outerHTML;
              } else {
                // Check if we're copying a nested tag
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = selectedHtml;
                const ssmlInSelection = tempDiv.querySelectorAll("[data-ssml]");

                // Check if the selection contains nested SSML elements
                if (ssmlInSelection.length > 0) {
                  // Allow copying both nested tags and parent tags
                  htmlToProcess = selectedHtml;
                } else {
                  // Selection is within an SSML element but doesn't contain the full element
                  const actualSelectedNode = editor.selection.getNode();
                  const closestSSML = actualSelectedNode.closest("[data-ssml]");

                  if (
                    closestSSML &&
                    selectedTextTrimmed ===
                    (closestSSML.textContent || "").trim()
                  ) {
                    htmlToProcess = (closestSSML as HTMLElement).outerHTML;
                  } else {
                    const allSSMLInBody = Array.from(
                      editorBody.querySelectorAll("[data-ssml]")
                    );
                    const matchingSSML: Element[] = [];

                    allSSMLInBody.forEach((ssmlEl) => {
                      const ssmlText = (ssmlEl.textContent || "").trim();
                      if (ssmlText === selectedTextTrimmed) {
                        matchingSSML.push(ssmlEl);
                      }
                    });

                    // Get the innermost matching elements (avoid nested duplicates)
                    const innermostSSML = matchingSSML.filter((el) => {
                      return !matchingSSML.some(
                        (otherEl) => otherEl !== el && otherEl.contains(el)
                      );
                    });

                    if (innermostSSML.length > 0) {
                      htmlToProcess = innermostSSML
                        .map((el) => (el as HTMLElement).outerHTML)
                        .join("");
                    } else {
                      htmlToProcess = selectedHtml;
                    }
                  }
                }
              }
            } else {
              // No parent SSML, use selected HTML as-is
              htmlToProcess = selectedHtml;
            }
          }
        }

        // Process the HTML to convert to SSML
        let processedHtml = htmlToProcess;
        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = htmlToProcess;

        // Remove only <p> tags while preserving their content and other tags
        const pTags = tempContainer.querySelectorAll("p");
        pTags.forEach((p) => {
          const parent = p.parentNode;
          if (parent) {
            while (p.firstChild) {
              parent.insertBefore(p.firstChild, p);
            }
            // Remove the empty <p> tag
            parent.removeChild(p);
          }
        });

        processedHtml = tempContainer.innerHTML;

        const decoded = decodeUmlautEntitiesOnly(processedHtml);
        const withoutNbsp = decoded.replace(/&nbsp;/g, " ");
        // Strip before wrapping to prevent double <speak>
        contentToCopy = `<speak>${stripSpeakTags(transformToSSML(withoutNbsp, false))}</speak>`;

        if (!contentToCopy || !contentToCopy.trim()) {
          return;
        }

        const validationResult = validateSSML(contentToCopy, false, t, {
          dynamicAllowedValues: {
            voiceNames,
            styleNames: emotionNames,
            languageValues,
          },
        });

        if (!validationResult.valid) {
          setNotification({
            severity: "error" as AlertColor,
            message: t("tts.invalidSsmlCopy"),
          });
          navigator.clipboard.writeText("").catch(() => { });
          return;
        }

        navigator.clipboard.writeText(contentToCopy);
      }
    };

    const handleNodeChange = () => {
      setActiveSSMLTags((prev) =>
        prev.filter((tag) =>
          editor.getBody()?.querySelector(`[data-ssml="${tag}"]`)
        )
      );
    };

    const handleClick = ({ target }: MouseEvent) => {
      const tag = (target as HTMLElement)
        .closest("[data-ssml]")
        ?.getAttribute("data-ssml");
      setActiveSSMLTags(tag ? [tag] : []);
    };

    editor.on("NodeChange", handleNodeChange);
    editorBody.addEventListener("click", handleClick);
    editorBody.addEventListener("copy", handleCopy);

    return () => {
      editor.off("NodeChange", handleNodeChange);
      const body = editor.getBody();
      if (body) {
        body.removeEventListener("click", handleClick);
        body.removeEventListener("copy", handleCopy);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editorRef,
    setActiveSSMLTags,
    isHtmlView,
    setNotification,
    t,
    ssmlViewEditable,
    voiceNames,
    emotionNames,
  ]);

  // This function is being used for the toolbar Copy button
  const performAction = () => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    if (action === "copy") {
      // Get HTML content
      const htmlContent = editor.getContent({ format: "html" });
      const textContent = editor.getContent({ format: "text" });

      // If editor is empty, don't copy
      if (!textContent.trim()) {
        return;
      }

      let contentToCopy = "";

      // Check if content contains ANY SSML tags
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const hasSSMLTags = tempDiv.querySelector("[data-ssml]") !== null;

      if (!isHtmlView || !hasSSMLTags) {
        contentToCopy = hasSSMLTags
          ? `<speak>${stripSpeakTags(
            transformToSSML(
              decodeUmlautEntitiesOnly(
                htmlContent.replace(/<\/?p[^>]*>/gi, "")
              ).replace(/&nbsp;/g, " "),
              false
            )
          )}</speak>`
          : `<speak>${stripSpeakTags(textContent)}</speak>`;
      } else {
        let processedHtml = htmlContent;

        // Unwrap spans
        {
          const parser = new DOMParser();
          const doc = parser.parseFromString(processedHtml, "text/html");
          const root = (doc.body || doc.documentElement) as HTMLElement;
          const spans = Array.from(root.querySelectorAll("span"));
          spans.forEach((span) => {
            const parent = span.parentNode;
            if (!parent) return;
            while (span.firstChild) parent.insertBefore(span.firstChild, span);
            parent.removeChild(span);
          });
          processedHtml = root.innerHTML;
        }

        // Use DOM manipulation to remove <p> tags instead of regex
        {
          const tempContainer = document.createElement("div");
          tempContainer.innerHTML = processedHtml;

          // Remove only <p> tags while preserving their content and SSML tags
          const pTags = tempContainer.querySelectorAll("p");
          pTags.forEach((p) => {
            const parent = p.parentNode;
            if (parent) {
              while (p.firstChild) {
                parent.insertBefore(p.firstChild, p);
              }
              parent.removeChild(p);
            }
          });

          processedHtml = tempContainer.innerHTML;
        }

        // Decode HTML entities
        const decoded = decodeUmlautEntitiesOnly(processedHtml);

        // Replace &nbsp; with regular spaces
        const withoutNbsp = decoded.replace(/&nbsp;/g, " ");

        // Transform to SSML format
        contentToCopy = `<speak>${stripSpeakTags(transformToSSML(withoutNbsp, false))}</speak>`;
      }

      const validationResult = validateSSML(contentToCopy, false, t, {
        dynamicAllowedValues: {
          voiceNames,
          styleNames: emotionNames,
          languageValues,
        },
      });

      if (!validationResult.valid) {
        setNotification({
          severity: "error" as AlertColor,
          message: t("tts.invalidSsmlCopy"),
        });
        return;
      }

      navigator.clipboard.writeText(contentToCopy).then(() => {
        setNotification({
          severity: "success",
          message: t("tts.scriptCopiedMessage"),
        });
      });
    } else if (action === "clear") {
      editor.undoManager.add();
      editor.setContent("");
      editor.focus();
      setActiveSSMLTags([]);
      onClear?.();
    }
  };

  const IconComponent = action === "copy" ? CopyIcon : DeleteIcon;
  const title = t(`tts.toolbarMenu.${action}`);
  const editorEmpty = isEditorEmpty(editorRef);

  const getColor = () => {
    if (editorEmpty) {
      return TTSCustomEditorColors.IconDisabledColor;
    } else {
      return TTSCustomEditorColors.ToolbarIconColor;
    }
  };

  return (
    <>
      <CustomIconMenu
        title={title}
        id={`${action}-button`}
        isActive={false}
        component={IconComponent}
        onClick={!editorEmpty ? performAction : undefined}
        color={getColor()}
        buttonStyles={{
          cursor: editorEmpty ? "default" : "pointer",
        }}
      />
    </>
  );
}
